import { Download } from "@phosphor-icons/react";
import { IG_CAROUSEL_SLIDES, IG_COMPLETE_SLIDES, IG_STORIES, REELS, FLYERS, tributeAssetUrl } from "./pressData";

function useAdminToken() {
  try { return typeof window !== "undefined" ? (window.localStorage.getItem("sws_admin_token") || "") : ""; } catch { return ""; }
}

// Group the 3 themed carousels by set prefix for magazine-style browsing
const CAROUSEL_SETS = [
  { id: "personal-story", title: "Carousel v1 · Personal Story", subtitle: "7 slides · AJ → Timmy → Harry → the ritual → CTA", prefix: "PS " },
  { id: "the-challenge", title: "Carousel v2 · The Challenge", subtitle: "7 slides · 911 min → pacing → merch/donate → wall → CTA", prefix: "CH " },
  { id: "donation", title: "Carousel v3 · Donation Drive", subtitle: "7 slides · 100% → T2T → FDNY → 50/50 → give", prefix: "DN " },
];

function CarouselGrid({ slides, testidPrefix }) {
  const token = useAdminToken();
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {slides.map((s) => {
        const url = tributeAssetUrl(s.src, token);
        return (
        <a
          key={s.src}
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          download
          data-testid={`${testidPrefix}${s.src.split("/").pop().replace(".png", "")}`}
          className="group block border border-white/15 hover:border-[#B32027] bg-black overflow-hidden transition-colors"
        >
          <div className="aspect-[4/5] overflow-hidden bg-black">
            <img src={url} alt={s.label} loading="lazy" className="w-full h-full object-cover" />
          </div>
          <p className="text-white/80 text-[10px] uppercase tracking-[0.2em] font-bold p-3 group-hover:text-[#B32027] flex items-center gap-2">
            {s.label} <Download size={11} weight="bold" className="ml-auto" />
          </p>
        </a>
      );})}
    </div>
  );
}

export default function InstagramAssets() {
  const token = useAdminToken();
  return (
    <>
      {/* Master Carousel · Combined story */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ Master carousel</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl">14 slides. The whole story.<br /><span className="text-[#B32027]">Beginning to end.</span></h2>
          </div>
          <p className="text-white/55 text-xs uppercase tracking-[0.2em] font-bold max-w-xs md:text-right">1080×1350 · magazine editorial · long-press to save each slide, or grab the ZIP.</p>
        </div>
        <CarouselGrid slides={IG_COMPLETE_SLIDES} testidPrefix="press-complete-" />
      </section>

      {/* Themed carousels */}
      {CAROUSEL_SETS.map((set) => {
        const setSlides = IG_CAROUSEL_SLIDES.filter((s) => s.src.includes(`/${set.id}/`));
        return (
          <section key={set.id} className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
            <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
              <div>
                <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ {set.title}</p>
                <h3 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-4xl">{set.subtitle}</h3>
              </div>
            </div>
            <CarouselGrid slides={setSlides} testidPrefix={`press-carousel-${set.id}-`} />
          </section>
        );
      })}

      {/* Instagram Stories */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ Instagram stories</p>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-6">Story frames.<br /><span className="text-[#B32027]">Tap to save.</span></h2>
        <div className="grid grid-cols-3 gap-3 max-w-3xl">
          {IG_STORIES.map((s) => {
            const url = tributeAssetUrl(s.src, token);
            return (
            <a
              key={s.src}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              download
              className="group block border border-white/15 hover:border-[#B32027] bg-black overflow-hidden transition-colors"
            >
              <div className="aspect-[9/16] overflow-hidden bg-black">
                <img src={url} alt={s.label} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <p className="text-white/80 text-[10px] uppercase tracking-[0.2em] font-bold p-3 group-hover:text-[#B32027] flex items-center gap-2">{s.label} <Download size={11} weight="bold" className="ml-auto" /></p>
            </a>
          );})}
        </div>
        <p className="text-white/55 text-xs uppercase tracking-[0.2em] font-bold mt-4">1080×1920 · silent · add a link sticker + your voice.</p>
      </section>

      {/* Flyers */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ Main flyer · 3 formats</p>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-6">Print it.<br /><span className="text-[#B32027]">Post it. Send it.</span></h2>
        <div className="grid md:grid-cols-3 gap-4">
          {FLYERS.map((flyer) => {
            const url = tributeAssetUrl(flyer.src, token);
            return (
            <a
              key={flyer.src}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              download
              data-testid={`press-flyer-${flyer.src.split("/").pop().replace(".png","")}`}
              className="group block border border-white/15 hover:border-[#B32027] bg-black overflow-hidden transition-colors"
            >
              <div className="aspect-[4/5] overflow-hidden bg-black">
                <img src={url} alt={flyer.label} loading="lazy" className="w-full h-full object-contain" />
              </div>
              <div className="p-4 space-y-1">
                <p className="text-white text-[11px] uppercase tracking-[0.2em] font-bold group-hover:text-[#B32027] flex items-center gap-2">{flyer.label} <Download size={11} weight="bold" className="ml-auto" /></p>
                <p className="text-white/55 text-[10px] leading-relaxed">{flyer.meta}</p>
              </div>
            </a>
          );})}
        </div>
      </section>

      {/* Reels */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ Ready-to-post reels</p>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-3">Tap play.<br /><span className="text-[#B32027]">Download. Post.</span></h2>
        <p className="text-white/70 text-base leading-relaxed max-w-2xl mb-8">
          Two vertical reels · 1080×1920 · no watermark · silent. Drop into CapCut / InShot and add your voiceover or trending audio.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {REELS.map((r) => {
            const url = tributeAssetUrl(r.src, token);
            return (
            <div
              key={r.src}
              data-testid={`press-reel-${r.src.split("/").pop().replace(".mp4","")}`}
              className="border border-white/15 bg-black overflow-hidden"
            >
              <div className="aspect-[9/16] bg-black">
                <video src={url} controls playsInline muted loop preload="metadata" className="w-full h-full object-cover bg-black" />
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="font-display font-black uppercase tracking-tighter text-lg leading-tight">{r.label}</p>
                  <p className="text-white/55 text-[10px] uppercase tracking-[0.2em] font-bold mt-1">{r.meta}</p>
                </div>
                <a
                  href={url}
                  download
                  className="inline-flex items-center gap-2 border-2 border-[#B32027] text-[#B32027] hover:bg-[#B32027] hover:text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors w-full justify-center"
                >
                  <Download size={12} weight="bold" /> Download .mp4
                </a>
              </div>
            </div>
          );})}
        </div>
      </section>
    </>
  );
}
